import { Builder } from '@builder.io/react';
import Script from 'next/script';
import { Fragment, useState } from 'react';
import useSWR from 'swr';
import { COLOR_KEYS } from '../../constants';
import {
  EventAction,
  EventName,
  InteractionName,
  useAvroTracking,
  useTrackEvent,
} from '../../observability';
import type { FormSubmissionResponse } from '../../pages/api/pagebuilder/form/[account]/[formID]';
import {
  FormBlock,
  FormDefinition,
  FormField,
  FormValues,
  fetchFormDefinition,
} from '../../rest';
import { Form, FormProps } from '../Form';
import { RichTextHtml } from '../RichTextHtml/RichTextHtml';

declare global {
  interface Window {
    ChiliPiper?: any;
  }
}

export type PrefilledField = {
  name: string;
  value?: string;
  cookieToBind?: string;
  isHiddenIfPrefilled: boolean;
};

export interface HubSpotFormV2Props
  extends Omit<FormProps, 'description' | 'confirmationMessage'> {
  description?: string;
  account: string;
  formId: string;
  prefilledFields?: PrefilledField[];
  enableChiliPiper?: boolean;
  chiliPiperRouter?: string;
}

export type AsyncProps = {
  defaultValues: HubSpotFormV2Props['defaultValues'];
  blocks: HubSpotFormV2Props['blocks'];
  submitButtonText: HubSpotFormV2Props['submitButtonText'];
};

export function HubSpotFormV2(props: HubSpotFormV2Props) {
  const { emitUserInteractionEvent } = useAvroTracking();
  const trackEvent = useTrackEvent();

  const {
    blocks,
    defaultValues: asyncDefaultValues,
    submitButtonText,
  } = useAsyncProps(props);

  const { defaultValues, cookieBindings } = getDefaults({
    blocks,
    defaultValues: asyncDefaultValues,
    prefilledFields: props.prefilledFields,
  });

  const [formError, setFormError] = useState<string>();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>();
  const [confirmationMessage, setConfirmationMessage] = useState<string>();
  const [isDisabled, setIsDisabled] = useState(false);

  const handleSubmit = async (values: FormValues) => {
    setIsDisabled(true);

    const response = await fetch(
      `/api/pagebuilder/form/${props.account}/${props.formId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      },
    );

    const data: FormSubmissionResponse | undefined = await response
      .json()
      .catch((error) => console.error(error));

    setIsDisabled(false);
    setFormError(data?.formError);
    setFieldErrors(data?.fieldErrors);

    if (!response.ok || !data?.confirmation) return;

    emitUserInteractionEvent({
      event_action: EventAction.click,
      interaction_name: InteractionName.submit,
      interaction_raw_text: submitButtonText || 'Submit',
      option_selected: 'submit',
      custom_tracking_fields: {
        form_title: props.title,
      },
    });

    trackEvent(EventName.HUBSPOT_FORM_SUBMITTED, {
      title: props.title,
      hubspot_form_id: props.formId,
      path: window.location.pathname,
    });

    if (
      props.enableChiliPiper &&
      props.chiliPiperRouter &&
      window.ChiliPiper?.submit
    ) {
      window.ChiliPiper.submit('zapier', props.chiliPiperRouter, {
        lead: values,
      });
    } else if (data.confirmation.type === 'redirect') {
      setIsDisabled(true);
      window.location.assign(data.confirmation.value);
    } else {
      setConfirmationMessage(data.confirmation.value);
    }
  };

  return (
    <Fragment>
      <Form
        backgroundColor={props.backgroundColor}
        cookieBindings={cookieBindings}
        title={props.title}
        blocks={blocks}
        defaultValues={defaultValues}
        error={formError}
        fieldErrors={fieldErrors}
        submitButtonText={submitButtonText}
        submitButtonColor={props.submitButtonColor}
        disableSpacing={props.disableSpacing}
        isDisabled={isDisabled}
        onSubmit={handleSubmit}
        {...{
          ...(props.description && {
            description: (
              <RichTextHtml
                html={props.description}
                backgroundColor={COLOR_KEYS.neutral100}
              />
            ),
          }),
          ...(confirmationMessage && {
            confirmationMessage: (
              <RichTextHtml
                html={confirmationMessage}
                backgroundColor={COLOR_KEYS.neutral100}
              />
            ),
          }),
        }}
      />
      {props.enableChiliPiper && (
        <Script src="https://js.chilipiper.com/marketing.js" />
      )}
    </Fragment>
  );
}

export function transformStringifiedValue(type: FormField['type'], value: any) {
  if (type === 'checkbox') {
    return value === 'true' || value === '1';
  }

  if (type === 'checkbox-group' && typeof value === 'string') {
    return value.split(',').map((s) => s.trim());
  }

  return value;
}

export function getFieldTypesMap(
  blocks: FormBlock[],
): Record<FormField['name'], FormField['type']> {
  return (
    blocks
      ?.flatMap((block) => block.fields)
      ?.reduce((acc, curr) => {
        if (curr?.name) return { ...acc, [curr?.name]: curr.type };
        return acc;
      }, {}) || {}
  );
}

export const getDefaults = (props: {
  blocks: HubSpotFormV2Props['blocks'];
  prefilledFields: HubSpotFormV2Props['prefilledFields'];
  defaultValues: HubSpotFormV2Props['defaultValues'];
}) => {
  const fieldTypesMap = getFieldTypesMap(props.blocks || []);

  const prefilledFields = (
    (props.prefilledFields as HubSpotFormV2Props['prefilledFields']) || []
  ).reduce(
    (acc, field) => {
      if (!field.name) {
        return acc;
      }

      const type = fieldTypesMap[field.name];

      const value =
        typeof field.value === 'string'
          ? transformStringifiedValue(type, field.value)
          : field.value;

      return {
        defaultValues: { ...acc.defaultValues, [field.name]: value },
        cookieBindings: {
          ...acc.cookieBindings,
          ...(field.cookieToBind ? { [field.name]: field.cookieToBind } : {}),
        },
      };
    },
    { defaultValues: {}, cookieBindings: {} },
  );

  return {
    defaultValues: {
      ...props.defaultValues,
      ...prefilledFields.defaultValues,
    },
    cookieBindings: prefilledFields.cookieBindings,
  };
};

export async function fetchAsyncProps(props: HubSpotFormV2Props) {
  const hubspotAccessTokenMap: Record<string, string | undefined> = {
    zapier: process.env.HUBSPOT_ACCESS_TOKEN_ZAPIER,
    sandbox: process.env.HUBSPOT_ACCESS_TOKEN_SANDBOX,
  };

  const hubspotAccessToken = hubspotAccessTokenMap[props.account];

  if (!hubspotAccessToken) {
    throw new Error('Invalid HubSpot account or missing access token');
  }
  const { defaultValues, blocks, submitButtonText } = await fetchFormDefinition(
    props.formId,
    hubspotAccessToken,
    props.prefilledFields,
  );

  return { defaultValues, blocks, submitButtonText };
}

const useAsyncProps = (props: HubSpotFormV2Props): AsyncProps => {
  const { data } = useSWR<FormDefinition>(
    `hubspot-form/${props.formId}`,
    async () => {
      if (Builder.isEditing || Builder.isPreviewing) {
        const response = await fetch(
          `/api/pagebuilder/form/${props.account}/${props.formId}`,
        );
        return response.json();
      }
    },
  );

  return (
    data || {
      blocks: props.blocks,
      defaultValues: props.defaultValues,
      submitButtonText: props.submitButtonText,
    }
  );
};
